import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import { journeyApi } from '../../api/journeyApi';
import { journeySituationApi } from '../../api/journeySituationApi';
import { useAuth } from '../../auth/AuthContext';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { collectMemberIds } from '../member-display/memberDisplayModel';
import { WORKSPACE_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import {
  SITUATION_SEVERITIES,
  SITUATION_TYPES,
  assigneeLabel,
  buildCreateSituationBody,
  canCreateSituation,
  classifySituationError,
  createSituationClientRequestId,
  partitionSituationList,
  situationErrorMessage,
  unwrapSituationList,
} from './journeySituationModel';
import '../journey-workspace/journey-workspace.css';
import './journey-situation.css';

function SituationCard({ item, summaries }) {
  return (
    <li>
      <Link className="journey-situation__card" to={item.detailRoute}>
        <p className="journey-situation__card-title">{item.title}</p>
        <p className="journey-situation__meta">
          <span>
            {item.situationStatusLabel}
            <span className="visually-hidden"> ({item.situationStatus})</span>
          </span>
          {' · '}
          <span>{item.situationTypeLabel || item.situationType}</span>
          {item.severityLabel ? ` · ${item.severityLabel}` : ''}
        </p>
        {item.assignedToMemberId ? (
          <p className="journey-situation__wrap">{assigneeLabel(item.assignedToMemberId, summaries)}</p>
        ) : null}
        {item.detectedAt ? (
          <p>
            <time dateTime={item.detectedAt}>{formatDateTime(item.detectedAt)}</time>
          </p>
        ) : null}
      </Link>
    </li>
  );
}

function SituationGroup({ headingId, title, items, empty, summaries }) {
  return (
    <section className="journey-situation" aria-labelledby={headingId}>
      <h3 id={headingId}>{title}</h3>
      {items.length === 0 ? (
        <p className="journey-situation__hint" role="status">{empty}</p>
      ) : (
        <ul className="journey-situation__list">
          {items.map((item) => (
            <SituationCard key={item.situationId} item={item} summaries={summaries} />
          ))}
        </ul>
      )}
    </section>
  );
}

export default function JourneySituationsPage() {
  const { journeyId } = useParams();
  const { user } = useAuth();
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const creating = useRef(false);
  const createRequestIdRef = useRef(null);
  const titleId = useId();
  const typeId = useId();
  const severityId = useId();
  const descriptionId = useId();

  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [journey, setJourney] = useState(null);
  const [items, setItems] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [pending, setPending] = useState(false);
  const [formError, setFormError] = useState('');
  const [draft, setDraft] = useState({
    title: '', situationType: 'OTHER', severity: 'MEDIUM', description: '',
  });
  const memberSummaries = useMemberDisplayNames(collectMemberIds([
    ...(journey?.members || []).map((member) => member.memberId),
    ...items.map((item) => item.assignedToMemberId),
  ]));

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setError('');
    }
    try {
      const [detailPayload, listPayload] = await Promise.all([
        journeyApi.getDetail(journeyId),
        journeySituationApi.list(journeyId),
      ]);
      if (requestId !== generation.current) return;
      setJourney(normalizeJourneyWorkspace(detailPayload, viewerMemberId));
      setItems(unwrapSituationList(listPayload));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifySituationError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'not-found' || access === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || access === 'forbidden') setPhase('forbidden');
      else {
        setError(situationErrorMessage(cause, '旅程狀況無法載入。'));
        setPhase('error');
      }
    }
  }, [journeyId, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  async function submitCreate(event) {
    event.preventDefault();
    if (!canCreate || creating.current) return;
    if (!String(draft.title || '').trim()) {
      setFormError('請填寫標題。');
      return;
    }
    if (!createRequestIdRef.current) {
      createRequestIdRef.current = createSituationClientRequestId();
    }
    creating.current = true;
    setPending(true);
    setFormError('');
    try {
      await journeySituationApi.create(
        journey.journeyId,
        buildCreateSituationBody(draft, { clientRequestId: createRequestIdRef.current }),
      );
      createRequestIdRef.current = null;
      setDraft({ title: '', situationType: 'OTHER', severity: 'MEDIUM', description: '' });
      setOpenForm(false);
      await load({ soft: true });
    } catch (cause) {
      setFormError(situationErrorMessage(cause, '無法建立旅程狀況。'));
    } finally {
      creating.current = false;
      setPending(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state" aria-live="polite">
        <p>載入狀況…</p>
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.notFoundTitle}</h1>
        <p>這趟行程無法查看，或已經移除。</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.forbiddenBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    return (
      <main className="journey-workspace-state" role="alert">
        <h1>無法開啟狀況</h1>
        <p>{error}</p>
        <button type="button" onClick={() => load()}>{WORKSPACE_PRODUCT_CHROME.tryAgain}</button>
      </main>
    );
  }

  const canCreate = canCreateSituation({
    role: journey?.requesterRole,
    lifecycleStatus: journey?.lifecycleStatus,
  });
  const groups = partitionSituationList(items);

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey?.title || '未命名行程'}</h1>
          <p className="journey-workspace__meta">
            {journey?.lifecycleProductLabel || journey?.lifecycleStatus}
            {journey?.requesterRoleLabel ? ` · ${journey.requesterRoleLabel}` : ''}
          </p>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />

      <section className="journey-workspace__panel journey-situation" aria-labelledby="situations-heading">
        <div className="journey-workspace__panel-head">
          <h2 id="situations-heading">旅程狀況</h2>
        </div>

        <div className="journey-situation__toolbar">
          {canCreate ? (
            <button type="button" className="journey-situation__primary" onClick={() => setOpenForm((v) => !v)}>
              {openForm ? '關閉表單' : '新增旅程狀況'}
            </button>
          ) : (
            <p className="journey-situation__hint">
              僅 READY / TRAVELING 的 ACTIVE 旅伴可以建立旅程狀況。
            </p>
          )}
        </div>

        {openForm && canCreate ? (
          <form className="journey-situation__form" onSubmit={submitCreate} noValidate>
            <label htmlFor={titleId}>
              標題
              <input
                id={titleId}
                value={draft.title}
                maxLength={100}
                disabled={pending}
                onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
              />
            </label>
            <label htmlFor={typeId}>
              類型
              <select
                id={typeId}
                value={draft.situationType}
                disabled={pending}
                onChange={(event) => setDraft((current) => ({ ...current, situationType: event.target.value }))}
              >
                {SITUATION_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
            <label htmlFor={severityId}>
              嚴重程度
              <select
                id={severityId}
                value={draft.severity}
                disabled={pending}
                onChange={(event) => setDraft((current) => ({ ...current, severity: event.target.value }))}
              >
                {SITUATION_SEVERITIES.map((severity) => (
                  <option key={severity} value={severity}>{severity}</option>
                ))}
              </select>
            </label>
            <label htmlFor={descriptionId}>
              說明（選填）
              <textarea
                id={descriptionId}
                maxLength={1000}
                value={draft.description}
                disabled={pending}
                onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
              />
            </label>
            {formError ? <p className="journey-workspace__error" role="alert">{formError}</p> : null}
            <button type="submit" className="journey-situation__primary" disabled={pending}>
              {pending ? '建立中…' : '建立狀況'}
            </button>
          </form>
        ) : null}

        {items.length === 0 ? (
          <div className="journey-situation__empty" role="status">
            <p>目前沒有需要處理的旅程狀況</p>
          </div>
        ) : (
          <>
            <SituationGroup headingId="open-situations" title="OPEN" items={groups.open} empty="目前沒有 OPEN 狀況。" summaries={memberSummaries} />
            <SituationGroup headingId="resolved-situations" title="RESOLVED" items={groups.resolved} empty="尚無 RESOLVED 狀況。" summaries={memberSummaries} />
            <SituationGroup headingId="cancelled-situations" title="CANCELLED" items={groups.cancelled} empty="尚無 CANCELLED 狀況。" summaries={memberSummaries} />
            {groups.other.length ? (
              <SituationGroup headingId="other-situations" title="其他狀態" items={groups.other} empty="" summaries={memberSummaries} />
            ) : null}
          </>
        )}
      </section>
    </main>
  );
}
